/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * THE ONE RULE TABLE for a pick's strip (Guillaume, 2026-10-21: "every placement reads the
 * same" — and one place to change it). A strip is the block `GuidePick` renders for a pick on
 * a guide, a review post or a plain post: under a Quick Picks roster row (`roster`), under an
 * `nc-deals-large-alt` card or inside an `nc-deals-large` card (`section`), under an "Our
 * picks" grid card (`grid`). `PickStrip` reads nothing about its placement except through
 * `stripRules`; `PickActions`, `PickSheet` and `PickOwners` take what it hands them.
 *
 * What is the same everywhere (not in the table): the action row — Pin to compare ·
 * Measurements · Owners, in that order, a slot the pick cannot honour greyed, never removed
 * (06a676f0); Owners opens the page's side panel at `lg`+ and the pick's sheet below, never a
 * block in place; the sheet's "This pick" shows the score chips the strip no longer prints.
 *
 * | placement | shell                     | chips | verdict | owned line | seat | hidden owners copy          | location |
 * | --------- | ------------------------- | ----- | ------- | ---------- | ---- | --------------------------- | -------- |
 * | roster    | attached (under the row)  | no    | no      | yes        | yes  | yes                         | roster   |
 * | section   | standalone / inCard       | no    | yes     | yes        | yes* | yes* (*no roster row)       | pick     |
 * | grid      | gridRow (compact glyphs)  | no    | no      | no         | no   | no                          | grid     |
 *
 * `hasRoster` on a section strip says the pick's roster row is on the page too: THAT strip
 * seats the ask and serves the crawlers' copy, this one does neither (one seat, one HTML copy
 * per pick — dcbf4ee5). `onGuide` is the page scope: the ask has a host (`GuideCapture`) on a
 * guide alone. Chips are `false` on every row since 2026-10-21 (the roster row lost them last,
 * the same day as the two cards); the column stays so the next change is one cell.
 *
 * Before any row: `stripRenders(context)` — the ONE gate (Guillaume, 2026-10-21). A pick whose
 * context has no lab curves (`lab_tested` false) and no owner review (count 0 / null) gets no
 * strip at all on any placement or surface — no greyed action row, no empty footer slot, no
 * spacing — and so does a pick DealHunt could not resolve. Scores alone render nothing anywhere
 * since the chips went. The callers that wrap the strip in a slot (`NCDealsLargeSG`'s
 * `footerSlot`, `gridActionsSlot`) ask this gate before building the slot, so the card body
 * renders exactly as it would without one.
 *
 * The surface gate on the crawlers' copy (Guillaume, 2026-10-21): on a REVIEW POST the page's
 * visible "What owners say" carousel is the crawlers' copy, so no strip serves a hidden one
 * there (`servesOwners` false under the `review_page` scope); best lists (both templates) and
 * plain posts keep it. The Owners tile and the panel are the same on every surface.
 */
import type { GuidePickContext } from 'lib/dealhunt/types'
import type { PickPlacement } from 'lib/owner-notes/lab-compare'
import type { CatalogPageType } from 'shared-code/app-events'

/** Whether a pick gets a strip at all: lab curves to open, or owner reviews to show. */
export function stripRenders(context: GuidePickContext | null | undefined): boolean {
  if (!context) {
    return false
  }
  return context.lab_tested || (context.owners?.count ?? 0) > 0
}

export type StripShell = 'attached' | 'standalone' | 'inCard' | 'gridRow'

export interface StripInput {
  placement: PickPlacement
  /** The pick's DealHunt context — `render` is `stripRenders(context)`. */
  context: GuidePickContext | null
  /** A section strip whose pick also has a roster row on the page (`DSNCOwnerGuidePick.hasRoster`). */
  hasRoster: boolean
  /** A section strip rendered as its `nc-deals-large` card's footer (`DSNCOwnerGuidePick.inCard`). */
  inCard: boolean
  /** The page scope (`CatalogAnalyticsProvider`): `guide_page` hosts the ask; `review_page`
   *  has its own visible crawlers' copy. Null outside a provider. */
  pageType: CatalogPageType | null
}

export interface StripRules {
  /** False: no strip at all (`stripRenders`) — every other field is moot. */
  render: boolean
  shell: StripShell
  /** The strip prints the score chip row above the actions. */
  chips: boolean
  /** The phone sheet's "This pick" segment gets the chip row. */
  sheetChips: boolean
  /** Declared and not the reader's own: the verdict sentence heads the strip. */
  verdict: boolean
  /** The reader's own pick: the "You own this" tag and their fingerprint. */
  ownedLine: boolean
  /** This strip may seat the page's one owner-review ask (the seat's own checks still apply). */
  seat: boolean
  /** This strip serves the pick's reviews `hidden` for crawlers — the page's one HTML copy. */
  servesOwners: boolean
  /** The `location` the strip's events report. */
  location: 'roster' | 'pick' | 'grid'
}

export function stripRules({
  placement,
  context,
  hasRoster,
  inCard,
  pageType,
}: StripInput): StripRules {
  const render = stripRenders(context)
  const onGuide = pageType === 'guide_page'
  // The review post's carousel is the crawlers' copy there.
  const crawlSurface = pageType !== 'review_page'
  switch (placement) {
    case 'roster':
      return {
        render,
        shell: 'attached',
        chips: false,
        sheetChips: true,
        verdict: false,
        ownedLine: true,
        seat: onGuide,
        servesOwners: crawlSurface,
        location: 'roster',
      }
    case 'section':
      return {
        render,
        shell: inCard ? 'inCard' : 'standalone',
        chips: false,
        sheetChips: true,
        verdict: true,
        ownedLine: true,
        seat: onGuide && !hasRoster,
        servesOwners: crawlSurface && !hasRoster,
        location: 'pick',
      }
    case 'grid':
      return {
        render,
        shell: 'gridRow',
        chips: false,
        sheetChips: true,
        verdict: false,
        ownedLine: false,
        seat: false,
        servesOwners: false,
        location: 'grid',
      }
  }
}
